import { container } from '@web/services/container';
import { type RetailerProfileOption, FetchRetailerProfileOptions } from '@packages/domain/techline/members';

import { useCallback, useState } from 'react';
import { useAbortController } from '@packages/core/http';

const [$fetchRetailerProfileOptions] = container.resolveOperations(FetchRetailerProfileOptions);

export function useRetailerProfileOptions() {
    const { abortSignalRef } = useAbortController();
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
    const [retailerProfiles, setRetailerProfiles] = useState<RetailerProfileOption[]>([]);
    const [selectedRetailerProfile, setSelectedRetailerProfile] = useState<RetailerProfileOption[]>([]);

    /**
     * Fetch retailer profile options for the authenticated user. When a single profile option is returned,
     * it is automatically selected. A retailer profile option represents a retailer the user is
     * associated with in Northstar and includes the retailer number.  A user can only create a
     * Techline case under a retailer they have a profile option for.
     */
    const fetchRetailerProfileOptions = useCallback(async () => {
        setIsLoadingProfiles(true);

        const retailers = await $fetchRetailerProfileOptions({}, abortSignalRef.current);

        if (!retailers) {
            setIsLoadingProfiles(false);
            return false;
        }

        setRetailerProfiles(retailers);

        if (retailers.length === 1) {
            setSelectedRetailerProfile(retailers);
        }

        setIsLoadingProfiles(false);
        return true;
    }, [abortSignalRef]);

    const resetRetailerProfileOptions = useCallback(() => {
        setRetailerProfiles([]);
        setSelectedRetailerProfile([]);
        setIsLoadingProfiles(false);
    }, []);

    return {
        fetchRetailerProfileOptions,
        isLoadingProfiles,
        resetRetailerProfileOptions,
        retailerProfiles,
        selectedRetailerProfile,
        setSelectedRetailerProfile,
    };
}
